import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import RegistrationForm from './RegistrationForm';
import MfaFlow from './MfaFlow';
import GoogleSignInButton from './GoogleSignInButton';
import LoginBanner from './LoginBanner';
import LanguageSwitcher from './LanguageSwitcher';
import { postWithRetry, describeFetchError, describeGatewayError } from '../utils/apiFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthPage({ onLoginSuccess }) {
  const { t } = useTranslation('auth');
  const [isLogin, setIsLogin] = useState(true);
  // Verified Google profile handed over by the Sign-In button when the
  // address has no account yet — pre-fills the registration form.
  const [googlePrefill, setGooglePrefill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  // Phase B — admin MFA gate. When login returns mfa_*_required, we
  // pivot to the MfaFlow screen instead of granting an access_token.
  const [mfaGate, setMfaGate] = useState(null);  // {mode:'setup'|'challenge', token, email, remember}

  // Login state
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    remember: false
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const { response, raw, data: parsed } = await postWithRetry(`${API_URL}/api/auth/login`, {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: loginData.email,
          password: loginData.password
        })
      });

      let data = parsed === null ? { detail: raw } : parsed;

      // Detect gateway/CDN error pages (Cloudflare 502/503, nginx 504, etc.)
      // where the server returns HTML instead of the JSON our API contract
      // guarantees. Surface a clean, human-readable message instead of
      // dumping raw markup into the login form.
      const looksLikeHtml = typeof data.detail === 'string' &&
        /<\s*(html|!doctype|head|body|title)\b/i.test(data.detail);
      if (looksLikeHtml) {
        data = { detail: null };  // fall through to the friendly message below
      }

      if (response.ok) {
        // ── Admin MFA gate (Phase B) ──────────────────────────
        // Backend may return one of two MFA challenges instead of
        // an access_token. We never set a token in this branch —
        // the MfaFlow does that after a successful TOTP/backup
        // code verification.
        if (data.mfa_setup_required && data.setup_token) {
          setMfaGate({ mode: 'setup', token: data.setup_token, email: data.email, remember: loginData.remember });
          setLoading(false);
          return;
        }
        if (data.mfa_required && data.challenge_token) {
          setMfaGate({ mode: 'challenge', token: data.challenge_token, email: data.email, remember: loginData.remember });
          setLoading(false);
          return;
        }

        if (loginData.remember) {
          localStorage.setItem('auth_token', data.access_token);
          localStorage.setItem('user', JSON.stringify(data.user));
        } else {
          sessionStorage.setItem('auth_token', data.access_token);
          sessionStorage.setItem('user', JSON.stringify(data.user));
        }

        setMessage({ type: 'success', text: 'Login successful! Redirecting...' });
        setTimeout(() => onLoginSuccess(data.user, data.access_token), 1000);
      } else {
        // Cloudflare / nginx / any upstream returning a gateway page
        // (502/503/504) means the backend is temporarily unreachable —
        // show a friendly message, not the parsed HTML.
        if ([502, 503, 504, 520, 521, 522, 523, 524].includes(response.status) || !data.detail) {
          setMessage({ type: 'error', text: describeGatewayError(response.status) });
        } else {
          // `data.detail` may be either a plain string or the structured
          // 500 envelope from our new user-validation-error handler:
          //   { code: 'user_serialization_failed', message: '…', error_id: '<hex>' }
          // When it's an object we render `message` + `error_id` so the
          // customer can quote a reference to support.
          let detail;
          if (typeof data.detail === 'string' && data.detail) {
            detail = data.detail;
          } else if (data.detail && typeof data.detail === 'object') {
            const parts = [data.detail.message || 'Login failed'];
            if (data.detail.error_id) parts.push(`Ref: ${data.detail.error_id}`);
            detail = parts.join(' — ');
          } else {
            detail = `Login failed (HTTP ${response.status})`;
          }
          setMessage({ type: 'error', text: detail });
        }
      }
    } catch (error) {
      // No usable HTTP response at all (blocked request, gateway timeout,
      // dropped connection). Say what actually happened instead of the
      // browser's bare "Failed to fetch".
      // eslint-disable-next-line no-console
      console.error('Login fetch failed:', error);
      setMessage({ type: 'error', text: describeFetchError(error) });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const { response, data } = await postWithRetry(
        `${API_URL}/api/auth/forgot-password?email=${encodeURIComponent(loginData.email)}`,
      );

      if (response.ok && data && data.message) {
        setMessage({ type: 'success', text: data.message });
        setTimeout(() => setShowForgotPassword(false), 3000);
      } else if (!response.ok) {
        // Real API error (JSON) vs. a gateway/HTML page with no JSON body.
        const detail = data && typeof data.detail === 'string' ? data.detail : null;
        setMessage({ type: 'error', text: detail || describeGatewayError(response.status) });
      } else {
        setMessage({
          type: 'success',
          text: 'If the email exists, a password reset link has been sent. It can take up to a minute to arrive.',
        });
        setTimeout(() => setShowForgotPassword(false), 3000);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Forgot-password fetch failed:', error);
      setMessage({ type: 'error', text: describeFetchError(error) });
    } finally {
      setLoading(false);
    }
  };

  if (mfaGate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <MfaFlow
          mode={mfaGate.mode}
          setupToken={mfaGate.mode === 'setup' ? mfaGate.token : undefined}
          challengeToken={mfaGate.mode === 'challenge' ? mfaGate.token : undefined}
          email={mfaGate.email}
          onSuccess={({ access_token, user }) => {
            if (mfaGate.remember) {
              localStorage.setItem('auth_token', access_token);
              localStorage.setItem('user', JSON.stringify(user));
            } else {
              sessionStorage.setItem('auth_token', access_token);
              sessionStorage.setItem('user', JSON.stringify(user));
            }
            setMfaGate(null);
            onLoginSuccess(user, access_token);
          }}
          onCancel={() => {
            setMfaGate(null);
            setMessage({ type: '', text: '' });
          }}
        />
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
          <div className="text-center mb-6">
            <img src="/logo.png" alt="AUTO-REMAP" width="479" height="102" className="w-56 sm:w-64 max-w-full h-auto mx-auto mb-4 object-contain" data-testid="forgot-password-logo" />
            <h2 className="text-2xl font-bold text-slate-800">Reset Password</h2>
            <p className="text-slate-600 mt-2">Enter your email to receive reset link</p>
          </div>

          {message.text && (
            <div className={`mb-4 p-4 rounded-lg ${
              message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleForgotPassword}>
            <div className="mb-6">
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  placeholder="Your email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                  className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>

            <button
              type="button"
              onClick={() => setShowForgotPassword(false)}
              className="w-full mt-4 py-3 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300 transition-colors"
            >
              Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Registration form is now a dedicated component
  if (!isLogin) {
    return (
      <RegistrationForm
        googlePrefill={googlePrefill}
        onBackToLogin={() => { setGooglePrefill(null); setIsLogin(true); }}
        onRegistered={() => setTimeout(() => { setGooglePrefill(null); setIsLogin(true); }, 3500)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 relative">
        {/* Language selector — top-right of the auth card. Visible on
            every auth surface (login, registration, forgot password,
            MFA) so the customer can pick their language before they
            even type the first character. */}
        <div className="absolute top-3 right-3" data-testid="auth-language-switcher">
          <LanguageSwitcher compact />
        </div>
        <LoginBanner />
        <div className="text-center mb-6">
          <img src="/logo.png" alt="AUTO-REMAP" width="479" height="102" className="w-56 sm:w-64 max-w-full h-auto mx-auto mb-4 object-contain" data-testid="auth-login-logo" />
          <h2 className="text-2xl font-bold text-slate-800">{t('welcome_back')}</h2>
          <p className="text-slate-600 mt-2">{t('login')} — AUTO-REMAP</p>
        </div>

        {message.text && (
          <div className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleLogin}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('fields.email')}</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
              <input
                type="email"
                placeholder={t('placeholders.email')}
                value={loginData.email}
                onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                required
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('fields.password')}</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                className="w-full pl-12 pr-12 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={loginData.remember}
                onChange={(e) => setLoginData({...loginData, remember: e.target.checked})}
                className="w-4 h-4 text-cyan-600 rounded focus:ring-cyan-500"
              />
              <span className="ml-2 text-sm text-slate-600">{t('remember_me')}</span>
            </label>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-cyan-600 hover:text-cyan-700"
            >
              {t('forgot_password')}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 mb-4"
          >
            {loading ? `${t('login')}…` : t('login')}
          </button>

          <p className="text-center text-sm text-slate-600">
            {t('no_account')}{' '}
            <button
              type="button"
              onClick={() => setIsLogin(false)}
              className="text-cyan-600 font-medium hover:text-cyan-700"
            >
              {t('create_account')}
            </button>
          </p>
        </form>

        {/* ── Google Sign-In (customers only) ─────────────── */}
        <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
          <span className="flex-1 h-px bg-slate-200" />
          <span>{t('or').toUpperCase()}</span>
          <span className="flex-1 h-px bg-slate-200" />
        </div>
        <GoogleSignInButton
          remember={loginData.remember}
          onSuccess={(user, token) => onLoginSuccess(user, token)}
          onSignupRequired={(prefill) => { setGooglePrefill(prefill); setIsLogin(false); }}
        />

        <div className="mt-6 pt-6 border-t border-slate-200 text-center text-xs text-slate-500">
          <p>© 2026 AR / AUTO-REMAP</p>
        </div>
      </div>
    </div>
  );
}
